import { useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { toast } from "sonner";
import { type CalendarEvent, EventCalendar, type EventColor } from "~/components/event-calendar";
import { useCalendarContext } from "~/components/event-calendar/calendar-context";
import { AppContainer } from "~/components/layouts/app/app-container";
import { Card } from "~/components/ui/card";
import { useTRPC } from "~/trpc/client";

export const Route = createFileRoute("/_app/podcasts/$channelUid/planning/")({
	component: Page,
});

function Page() {
	const params = Route.useParams();

	const { t } = useLingui();
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	const { data: episodes, isFetching } = useQuery(
		trpc.episode.list.queryOptions({
			channelUid: params.channelUid,
		}),
	);

	const deleteMutation = useMutation(trpc.episode.delete.mutationOptions());
	const moveMutation = useMutation(trpc.episode.move.mutationOptions());

	const events = useMemo(
		() =>
			(episodes ?? [])
				.map((episode) => {
					if (!episode.publishDate) {
						return null;
					}

					return {
						id: episode.uid,
						title: episode.title,
						description: episode.description,
						start: new Date(episode.publishDate),
						end: new Date(episode.publishDate),
						color: episode.themeColor as EventColor,
						label: episode.title,
						location: episode.channelsTitle,
						typeId: episode.typeID,
						seasonNum: episode.seasonNum,
						episodeNum: episode.episodeNum,
					};
				})
				.filter(Boolean),
		[episodes],
	);
	const { isColorVisible } = useCalendarContext();

	// Filter events based on visible colors
	const visibleEvents = useMemo(() => {
		return events.filter((event) => isColorVisible(event.color));
	}, [events, isColorVisible]);

	const handleEventDelete = async (eventId: string) => {
		await deleteMutation.mutateAsync(
			{ episodeUid: eventId },
			{
				onSuccess() {
					toast.success(t`Episode has been successfully deleted.`);
					queryClient.setQueryData(
						trpc.episode.listAll.queryKey(),
						episodes?.filter((episode) => episode.uid !== eventId),
					);
				},
			},
		);
	};

	const handleEventMove = async (event: CalendarEvent) => {
		await moveMutation.mutateAsync(
			{ uid: event.id, publicationDate: event.start.toISOString() },
			{
				onSuccess() {
					queryClient.setQueryData(trpc.episode.listAll.queryKey(), (episodes) =>
						episodes?.map((e) => {
							if (e.uid === event.id) {
								e.publishDate = event.start.toISOString();
							}

							return e;
						}),
					);
				},
			},
		);
	};

	return (
		<AppContainer className="my-12">
			<Card className="p-0">
				<EventCalendar
					events={visibleEvents}
					onEventDelete={handleEventDelete}
					onEventMove={handleEventMove}
					initialView="month"
					loading={isFetching}
				/>
			</Card>
		</AppContainer>
	);
}
